import React from "react";

interface PipelineStage {
  id: string;
  label: string;
  count: number;
  active?: boolean;
}

function isPipelineStage(value: unknown): value is PipelineStage {
  if (!value || typeof value !== "object") return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.id === "string" && typeof obj.label === "string" && typeof obj.count === "number"
  );
}

/**
 * Horizontal pipeline-stage flow. Payload shape mirrors the workspace
 * render-spec-compiler:
 *   { stages: Array<{ id, label, count, active? }>, objectKind? }
 *
 * When the agent emits an `objectKind` (e.g. "deal_stage"), each stage
 * becomes globally selectable via WorkspaceShell's data-object-*
 * protocol — clicking a stage emits an ObjectRef to the chat composer.
 * Without `objectKind`, stages render as presentation-only.
 *
 * Visual spec: see .pipeline / .pipeline-stage / .pipeline-count in
 * sandbox-styles.css. Stages share a baseline border, with border-radius
 * only on the outermost edges. Active stage gets primary-subtle treatment.
 */
export default function PipelineBlock({ payload }: { payload: Record<string, unknown> }) {
  const rawStages = Array.isArray(payload.stages) ? payload.stages : [];
  const stages = rawStages.filter(isPipelineStage);
  const objectKind = typeof payload.objectKind === "string" ? payload.objectKind : undefined;

  if (stages.length === 0) {
    return (
      <div
        aria-label="pipeline-empty"
        className="text-xs italic"
        style={{ color: "var(--c-text-faint)" }}
      >
        No pipeline stages.
      </div>
    );
  }

  return (
    <div aria-label="pipeline" className="flex overflow-x-auto" style={{ gap: 2 }}>
      {stages.map((stage, idx) => {
        const isFirst = idx === 0;
        const isLast = idx === stages.length - 1;
        const radius = isFirst && isLast ? 6 : isFirst ? "6px 0 0 6px" : isLast ? "0 6px 6px 0" : 0;
        const active = stage.active === true;
        return (
          <div
            key={stage.id}
            data-stage-id={stage.id}
            data-object-id={objectKind ? stage.id : undefined}
            data-object-kind={objectKind}
            data-active={active ? "true" : "false"}
            className="flex-1 min-w-0 text-center font-medium"
            style={{
              padding: "10px 12px",
              backgroundColor: active ? "var(--c-primary-subtle)" : "var(--c-surface)",
              border: active ? "1px solid var(--c-primary)" : "1px solid var(--c-border)",
              borderRadius: radius,
              fontSize: 11,
              color: active ? "var(--c-primary)" : "var(--c-text-secondary)",
              transition: "all 150ms ease",
            }}
          >
            <span
              className="block font-bold"
              style={{
                fontSize: 18,
                letterSpacing: "-0.02em",
                color: active ? "var(--c-primary)" : "var(--c-text)",
              }}
            >
              {stage.count}
            </span>
            {stage.label}
          </div>
        );
      })}
    </div>
  );
}
